import React from "react";
import { FiServer, FiCloud, FiChevronDown, FiLoader, FiSearch } from "react-icons/fi";
import WierszDyskuDoWniosku from "./WierszDyskuDoWniosku";
import s from "./ListaDyskowDoWniosku.module.css";

/* ════════════════════════════════════════════════════════════════════════════════════════
   LISTA ZASOBÓW W OKNIE „Utwórz nowe zgłoszenie → Zmiana uprawnień do dysków sieciowych”.

   [polecenia właściciela 2026-09-13]
   R19 „wypisuj poprawione nazwy, nazwę zasobu sieciowego bez RW […] a także pokazuj opis.”
   R20 „popraw UI, bo to jest za duże. Nie pokazuj właścicieli personalnie, pokazuj dział”

   ── CO ZMIENIŁO SIĘ WZGLĘDEM KART (mierzone na kodzie sprzed zmiany) ─────────────────────
   • Każdy dysk był KARTĄ z ikoną 34 px, trzema-czterema liniami tekstu i nazwiskiem opiekuna;
     każda grupa miała WŁASNY suwak (50vh), więc w oknie były dwa poziomy przewijania.
   • Teraz: jedna przewijana lista, wiersz ≈ 2,5 rem (`WierszDyskuDoWniosku`), nagłówek grupy
     PRZYKLEJONY (position: sticky) — przewijając 40 dysków jednego serwera, człowiek dalej widzi,
     w którym udziale jest.
   • Opiekun nie jest pokazywany w treści — tylko w podpowiedzi wiersza; w treści jest DZIAŁ.
   ════════════════════════════════════════════════════════════════════════════════════════ */

/** Etykieta nagłówka grupy — grupa „-” to zasoby bez adresu sieciowego. */
export const etykietaGrupyDyskow = (klucz) => {
    const k = String(klucz || "").trim();
    if (!k || k === "-") return "Bez adresu sieciowego";
    return k;
};

const formaDyskow = (n) => {
    if (n === 1) return "zasób";
    const d = n % 10;
    const s100 = n % 100;
    return d >= 2 && d <= 4 && (s100 < 12 || s100 > 14) ? "zasoby" : "zasobów";
};

/**
 * @param {object}   props
 * @param {Array}    props.grupy            `[{ masterDiskAddress, drives: [...] }]` z `/my-tickets/all-drives-grouped`
 * @param {boolean}  [props.ladowanie]
 * @param {string[]} props.wybrane          identyfikatory zaznaczonych zasobów
 * @param {object}   props.poziomy          `{ [id]: "read" | "read_write" }`
 * @param {object}   props.rozwiniete       `{ [klucz grupy]: boolean }`
 * @param {Function} props.onPrzelaczGrupe  (klucz)
 * @param {Function} props.onZaznacz        (id, boolean)
 * @param {Function} props.onPoziom         (id, poziom)
 */
const ListaDyskowDoWniosku = ({
    grupy, ladowanie = false, wybrane = [], poziomy = {}, rozwiniete = {},
    onPrzelaczGrupe, onZaznacz, onPoziom,
}) => {
    if (ladowanie) {
        return (
            <div className={s.stan} role="status" data-testid="dyski-wniosek-ladowanie">
                <FiLoader className={s.obrot} size={16} aria-hidden="true" />
                <span>Ładowanie dysków sieciowych...</span>
            </div>
        );
    }

    if (!Array.isArray(grupy) || grupy.length === 0) {
        return (
            <div className={s.stan} data-testid="dyski-wniosek-pusto">
                <FiSearch size={20} aria-hidden="true" />
                <span className={s.stanTytul}>Brak wyników</span>
                <span className={s.stanOpis}>Zmień frazę — szukamy po nazwie, opisie, dziale i adresie.</span>
            </div>
        );
    }

    return (
        <div className={s.lista} data-testid="dyski-wniosek-lista">
            {grupy.map((grupa) => {
                const klucz = grupa.masterDiskAddress;
                const dyski = Array.isArray(grupa.drives) ? grupa.drives : [];
                const otwarta = Boolean(rozwiniete[klucz]);
                const wybranych = dyski.filter((d) => wybrane.includes(d.id)).length;
                const witryny = dyski.length > 0 && dyski.every((d) => d.kind === "sharepoint");
                const etykieta = etykietaGrupyDyskow(klucz);

                return (
                    <section key={klucz || "-"} className={s.grupa} aria-label={etykieta}>
                        <button
                            type="button"
                            className={s.naglowek}
                            onClick={() => onPrzelaczGrupe?.(klucz)}
                            aria-expanded={otwarta}
                            data-testid="dyski-wniosek-grupa"
                        >
                            {witryny ? <FiCloud size={12} aria-hidden="true" /> : <FiServer size={12} aria-hidden="true" />}
                            <span className={s.naglowekAdres}>{etykieta}</span>
                            <span className={s.naglowekLiczba}>
                                {dyski.length} {formaDyskow(dyski.length)}
                            </span>
                            {wybranych > 0 && <span className={s.naglowekWybrane}>{wybranych} wybrano</span>}
                            <FiChevronDown
                                size={14}
                                className={`${s.chevron} ${otwarta ? s.chevronOtwarty : ""}`}
                                aria-hidden="true"
                            />
                        </button>

                        {otwarta && (
                            <div className={s.wiersze}>
                                {dyski.map((dysk) => (
                                    <WierszDyskuDoWniosku
                                        key={dysk.id}
                                        dysk={dysk}
                                        zaznaczony={wybrane.includes(dysk.id)}
                                        poziom={poziomy[dysk.id] || "read"}
                                        onZaznacz={onZaznacz}
                                        onPoziom={onPoziom}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
};

export default ListaDyskowDoWniosku;
